import React, { useState, useRef, useEffect } from 'react';
import { CaseStudyTaxonomies, TechnologyGroup } from '@/types/pages';
import Select from '@/components/ui/Select';
import styles from './CaseStudyFilters.module.css';

export interface ActiveFilters {
  industryId: string;
  offeringId: string;
  technologyId: string;
}

interface Props {
  taxonomies: CaseStudyTaxonomies;
  active: ActiveFilters;
  onChange: (filters: ActiveFilters) => void;
  onClear: () => void;
}

function TechnologySelect({
  groups,
  value,
  onChange,
}: {
  groups: TechnologyGroup[];
  value: string;
  onChange: (groupId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedTech = groups.flatMap((g) => g.technologies || []).find((t) => t._id === value);
  const label = selectedTech ? selectedTech.title : null;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative inline-block min-w-[140px]">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-a-border bg-white px-4 py-2 text-sm focus:outline-none"
      >
        <span className={`font-semibold ${label ? 'text-a-navy' : 'text-a-text-secondary'}`}>
          {label || 'Technology'}
        </span>
        <svg
          className={`h-3 w-3 flex-shrink-0 text-a-text-secondary transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className={`absolute left-0 top-full z-50 mt-1 rounded-lg border border-a-border bg-white shadow-lg ${styles.techDropdown}`}
        >
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className="w-full whitespace-nowrap px-4 pt-3 pb-2 text-left text-sm font-semibold text-a-navy transition-colors hover:bg-a-off-white"
            >
              All
            </button>
          )}
          {groups.map((group, i) => (
            <div key={group._id}>
              <p
                className={`mb-0 whitespace-nowrap px-4 pb-1 text-xs font-bold uppercase tracking-widest text-a-text-secondary ${
                  i === 0 ? 'pt-3' : 'mt-6 pt-0'
                }`}
              >
                {group.title}
              </p>
              {[...(group.technologies ?? [])]
                .sort((a, b) => a.title.trim().localeCompare(b.title.trim(), undefined, { sensitivity: 'base' }))
                .map((tech) => (
                  <button
                    key={tech._id}
                    type="button"
                    onClick={() => {
                      onChange(tech._id);
                      setOpen(false);
                    }}
                    className={`w-full whitespace-nowrap px-4 py-2 text-left text-sm transition-colors hover:bg-a-off-white ${value === tech._id ? 'font-semibold text-a-navy' : 'text-a-navy'}`}
                  >
                    {tech.title.trim()}
                  </button>
                ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CaseStudyFilters: React.FC<Props> = ({ taxonomies, active, onChange, onClear }) => {
  const hasActive = active.industryId || active.offeringId || active.technologyId;

  return (
    <div className="mb-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Select
          placeholder="Industry"
          options={[
            { value: '', label: 'All' },
            ...taxonomies.industries.map((t) => ({
              value: t._id,
              label: t.title,
            })),
          ]}
          value={active.industryId}
          onChange={(id) => onChange({ industryId: id, offeringId: '', technologyId: '' })}
        />
        <Select
          placeholder="Offering"
          options={[
            { value: '', label: 'All' },
            ...taxonomies.offerings.map((t) => ({
              value: t._id,
              label: t.title,
            })),
          ]}
          value={active.offeringId}
          onChange={(id) => onChange({ industryId: '', offeringId: id, technologyId: '' })}
        />
        {taxonomies.technologyGroups?.length > 0 && (
          <TechnologySelect
            groups={taxonomies.technologyGroups}
            value={active.technologyId}
            onChange={(id) =>
              onChange({
                industryId: '',
                offeringId: '',
                technologyId: active.technologyId === id ? '' : id,
              })
            }
          />
        )}
        {hasActive && (
          <button
            onClick={onClear}
            className="mt-2 flex-shrink-0 cursor-pointer text-sm font-semibold text-a-navy transition-colors hover:text-a-orange sm:mt-0"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
};

export default CaseStudyFilters;
